import { useEffect, useState } from 'react';
import { subscribeToCalls } from '../api/call-stream';
import { ApiError } from '../api/client';
import { useCallConfig, useInitiateCall } from '../api/hooks';
import type { CallLog, CallStatus } from '../api/types';

/* ------------------------------ formatting ------------------------------ */

/** Connected talk time. Ring time is deliberately excluded — see the schema. */
export function formatBillsec(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds === 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export const CALL_STATUS_LABEL: Record<CallStatus, string> = {
  initiated: 'Calling…',
  ringing: 'Ringing…',
  answered: 'Connected',
  completed: 'Completed',
  no_answer: 'No answer',
  busy: 'Busy',
  failed: 'Failed',
  congestion: 'Network busy',
};

/** Live states are the ones still moving; they drive the pulsing indicator. */
export const isLiveStatus = (status: CallStatus): boolean =>
  status === 'initiated' || status === 'ringing' || status === 'answered';

/**
 * Colour by outcome, using the same reserved tokens as the rest of the app.
 * Never colour alone — every use pairs this with the text label.
 */
export function callStatusColor(status: CallStatus): string {
  if (status === 'answered' || status === 'completed') return 'var(--good)';
  if (status === 'no_answer' || status === 'busy') return 'var(--warn)';
  if (status === 'failed' || status === 'congestion') return 'var(--danger)';
  return 'var(--muted)';
}

/* ------------------------------- the button ------------------------------ */

/**
 * Subscribes to the live stream and reports the newest update for one lead.
 *
 * The subscription is shared per mounted button. That is fine at the scale this
 * runs at — one lead detail page, or a list where only the clicked row has an
 * active call — and keeps the component self-contained.
 */
function useLiveCall(leadId: string, active: boolean): CallLog | null {
  const [call, setCall] = useState<CallLog | null>(null);

  useEffect(() => {
    if (!active) return;
    return subscribeToCalls((update) => {
      if (update.lead_id === leadId) setCall(update);
    });
  }, [leadId, active]);

  return call;
}

export function CallButton({
  leadId,
  phone,
  compact = false,
}: {
  leadId: string;
  phone: string | null;
  compact?: boolean;
}) {
  const { data: config } = useCallConfig();
  const initiate = useInitiateCall();
  const [started, setStarted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = useLiveCall(leadId, started);

  // No PBX, no device, or no permission — offering a control that can only
  // fail is worse than not offering one.
  if (!config?.can_call || !phone) return null;

  const status = live?.status ?? (started ? 'initiated' : null);
  const busy = initiate.isPending;

  async function place(e: React.MouseEvent) {
    // Lives inside a clickable table row on the lead list.
    e.stopPropagation();
    e.preventDefault();
    setError(null);
    try {
      await initiate.mutateAsync(leadId);
      setStarted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not place the call');
      setStarted(false);
    }
  }

  const label = status ? CALL_STATUS_LABEL[status] : 'Call';
  const settled = status !== null && !isLiveStatus(status);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button
        type="button"
        className={compact ? 'btn btn-ghost btn-sm' : 'btn btn-sm'}
        onClick={place}
        disabled={busy || (status !== null && isLiveStatus(status))}
        title={`Call ${phone}`}
        aria-label={`Call ${phone}`}
        style={settled ? { color: callStatusColor(status) } : undefined}
      >
        {busy ? <span className="spinner" /> : <span aria-hidden="true">☎</span>}
        <span style={{ marginLeft: 6 }}>{label}</span>
      </button>

      {/* The pulsing dot only ever appears next to a word, never instead of one. */}
      {status && isLiveStatus(status) && (
        <span
          className="dot"
          aria-hidden="true"
          style={{ background: callStatusColor(status), animation: 'pulse 1.4s ease-in-out infinite' }}
        />
      )}

      {live?.status === 'completed' && live.billsec !== null && (
        <span className="hint" style={{ margin: 0 }}>{formatBillsec(live.billsec)}</span>
      )}

      {error && (
        <span className="err" style={{ margin: 0 }} role="alert">{error}</span>
      )}
    </span>
  );
}
